import type { GetStaticPaths, GetStaticPathsContext, GetStaticProps, GetStaticPropsContext } from 'next';
import type { ReactElement } from 'react';
import type { CategoryRequestProps, GameProps, GameRequestProps, GameViewProps } from '../../types';
import { CollectionTypesEnum } from '../../types/enum';

import * as React from 'react';

import { View } from '../../generated/game';
import { BaseLayout } from '../../layouts/BaseLayout';

import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ArticleJsonLd, BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { getAllCollections, getCollection, getGame, getLatestGames, getPopularGames, getSEO } from '../../app/api';
import { mappingGame, mappingGameCrumbs, mappingGamesMinimal, mappingCollection } from '../../app/mappings';
import { useAppContext } from '../../contexts/AppContext';
import { getNonPageNamespaces } from '../../utils/get-non-page-namespaces';
import { getLocalizedPaths } from '../../utils/localize-helpers';
import { SubscriptionModal } from '../../components/SubscriptionModal';
import styled from '@emotion/styled';

const Script = styled('script')``;

export default function DynamicPage(props: GameViewProps) {
  const { game, title, pageContext } = props;
  const { t, i18n } = useTranslation(['common', 'game']);
  const appContext = useAppContext();
  const { name, url, assetAbsoluteUrl, appIconId, noGameFollow, componentOptions } = appContext.useConfig();
  const router = useRouter();
  const paths = getLocalizedPaths(router);
  const siteUrl = `${url?.replace(/\/+$/, '')}`;

  let metaTitle = game.meta?.title || title;
  let seoTitle = i18n.exists('game:title', { fallbackLng: pageContext.locale })
    ? t('game:title', { title: metaTitle })
    : t('common:title', { title: metaTitle });
  let metaDescription = game.meta?.description || game.description;
  let seoDescription = i18n.exists('game:description', { fallbackLng: pageContext.locale })
    ? t('game:description', { title: metaTitle, description: metaDescription })
    : t('common:description', { title: metaTitle, description: metaDescription });
  let seoContent;

  // Customized SEO
  const { seo } = appContext.useSeo();

  // Homepage page
  if (seo?.games) {
    const gamesSeo = Object.keys(seo.games);
    // Current game matches in SEO
    if (gamesSeo.includes(game.gdGameId) && router.locale) {
      const currentGame = seo.games[game.gdGameId];
      // Current game matches the same language
      if (currentGame[router.locale]) {
        // Current game matches the same language has title
        if (currentGame[router.locale]?.title) {
          seoTitle = currentGame[router.locale]?.title;
        }
        // Current game matches the same language has description
        if (currentGame[router.locale]?.description) {
          seoDescription = currentGame[router.locale]?.description;
        }
        // Current game already added seo description
        if (currentGame[router.locale]?.gameDescription) {
          seoContent = currentGame[router.locale]?.gameDescription;
        }
      }
    }
  }

  const pageContextWithPaths = { ...pageContext, paths, seoContent };

  const showSubscriptionModal = componentOptions.component_project_subscription_mode;
  const ogtitle = componentOptions?.component_facebook_og_title;
  const ogDescription = componentOptions?.component_facebook_og_description;

  let ssrSeo: any;

  if (props?.seo && props.seo[0]) {
    const seoObject = props.seo[0];
    if (seoObject.pages?.game && router.locale) {
      const seoCurrentLanguage = seoObject.pages.game[router.locale];
      if (seoCurrentLanguage.json_ld) {
        ssrSeo = seoCurrentLanguage.json_ld;
      }
    }
  }

  const languageAlternatives = getLocalizedPaths(router).map((path) => ({
    hrefLang: path.locale,
    href: ssrSeo?.url ? `${ssrSeo.url}${getLocalizedPaths(router)[0].path}` : `${url}${path.path}`,
  }));

  return (
    <>
      {showSubscriptionModal && <SubscriptionModal />}
      <NextSeo
        nofollow={noGameFollow ? true : false}
        noindex={noGameFollow ? true : false}
        title={seoTitle}
        description={seoDescription}
        languageAlternates={languageAlternatives.map((locale) => ({
          hrefLang: locale.hrefLang,
          href: locale.href,
        }))}
        canonical={`${ssrSeo?.url}${getLocalizedPaths(router)[0].path}`}
        openGraph={{
          title: ogtitle ? ogtitle : seoTitle,
          description: ogDescription ? ogDescription : seoDescription,
          url: componentOptions.component_facebook_og_url ? componentOptions.component_facebook_og_url : url,
          type: 'website',
          images: [
            {
              url: componentOptions?.component_facebook_og_image_url
                ? componentOptions.component_facebook_og_image_url
                : `${url}/games/media/${game.images.cover1x1 || game.images.cover}/?ar[r]=1x1&ar[w]=512&fmt=.png`,
              width: 512,
              height: 512,
              alt: `${seoTitle}`,
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      {ssrSeo && (
        <Script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@type': ssrSeo['@type'],
              '@context': ssrSeo['@context'] || 'https://schema.org',
              ...Object.keys(ssrSeo as any).reduce((acc: { [key: string]: any }, key: string) => {
                if (key !== '@context' && key !== '@type') {
                  acc[key] = (ssrSeo as any)[key];
                }
                return acc;
              }, {}),
              url: `${ssrSeo.url}${getLocalizedPaths(router)[0].path}`,
            }),
          }}
        />
      )}
      {ssrSeo && (
        <Script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@type': 'Game',
              '@context': ssrSeo['@context'] || 'https://schema.org',
              ...Object.keys(ssrSeo as any).reduce((acc: { [key: string]: any }, key: string) => {
                if (key !== '@context' && key !== '@type') {
                  acc[key] = (ssrSeo as any)[key];
                }
                return acc;
              }, {}),
            }),
          }}
        />
      )}
      {!componentOptions.component_collection_json_ld_is_disabled && (
        <ArticleJsonLd
          type="NewsArticle"
          url={`${siteUrl}${router.asPath}`}
          title={`${seoTitle}`}
          images={Object.values(game.images)
            .filter((image) => image !== null)
            .map((image) => `${assetAbsoluteUrl}${image}/`)}
          datePublished={`${game.publishedAt}`}
          authorName="GameDistribution"
          publisherName={`${name}`}
          publisherLogo={`${assetAbsoluteUrl}${appIconId}`}
          description={`${seoDescription}`}
        />
      )}

      {!componentOptions.component_breadcrumb_json_ld_is_disabled && (
        <BreadcrumbJsonLd
          itemListElements={pageContextWithPaths.breadcrumbs.map((crumb, index) => ({
            position: index + 1,
            name: crumb.name,
            item: crumb.href,
          }))}
        />
      )}
      <View {...props} pageContext={pageContextWithPaths} />
    </>
  );
}

// Adding getLayout property. Allowing us to return a React component for the layout.
// This allows us to define the layout on a per-page basis. Since we're returning a function,
// we can have complex nested layouts if desired.
DynamicPage.getLayout = function getLayout(page: ReactElement) {
  const { props } = page;
  const { pageContext } = props;

  return (
    <BaseLayout pageType="game" pageContext={pageContext}>
      {page}
    </BaseLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async (context: GetStaticPathsContext) => {
  // Request a bunch of our popular games by locale for static pre-rendering.
  // Always make sure we return the object below.
  // https://nextjs.org/docs/advanced-features/i18n-routing#dynamic-routes-and-getstaticprops-pages
  const locales: (string | undefined)[] = context?.locales || [context.defaultLocale];
  const promises: Promise<GameRequestProps[] | undefined>[] = locales.map((locale) =>
    getPopularGames(
      {
        _limit: 12,
        _fieldsGame: 'image', // Include "image" property for each game.
      },
      {
        ssr: true,
        locale,
      }
    )
  );
  const response: (GameRequestProps[] | undefined)[] = await Promise.all(promises);
  const localizedBatches: { id: string; slug: string; locale: string | undefined }[] =
    Array.isArray(response) &&
    (response.map((batch: GameRequestProps[] | undefined, index: number) => {
      return (
        Array.isArray(batch) &&
        batch.map((game: GameRequestProps) => {
          return {
            id: game.id,
            slug: game.code,
            locale: locales[index],
          };
        })
      );
    }) as any);

  const games = Array.prototype.concat.apply([], localizedBatches);

  return {
    paths: games.map((path: GameProps) => ({
      params: {
        slug: path.slug,
      },
      locale: path.locale,
    })),
    // Use server side rendering (SSR) when a page is not cached in our Next file system.
    // Let the user or search bot wait until then; before continuing the route to the designated page.
    // https://nextjs.org/docs/api-reference/data-fetching/get-static-paths#fallback-true
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
  const slug: string = (context?.params?.slug || '') as string;
  const locale: string = (context.locale || context.defaultLocale) as string;

  const game: GameRequestProps | undefined = await getGame(
    slug,
    {
      _fieldsGame: 'image',
    },
    {
      ssr: true,
      locale,
    }
  );
  const popular: CategoryRequestProps | undefined = await getCollection(
    `${process.env.PROJECT_CODE}-popular`,
    CollectionTypesEnum.COLLECTION,
    {
      _fieldsGame: 'image',
    },
    {
      ssr: true,
      locale,
    }
  );
  const category: CategoryRequestProps | undefined = await getCollection(
    (Array.isArray(game?.categories) && game?.categories[0]?.code) || '',
    CollectionTypesEnum.CATEGORY,
    {
      period: 'week',
      _limit: 12,
      _gameRandom: true,
    },
    {
      ssr: true,
      locale,
    }
  );

  const collections: CategoryRequestProps[] | undefined = await getAllCollections(
    CollectionTypesEnum.COLLECTION,
    {
      _fieldsGame: 'image',
    },
    {
      ssr: true,
      locale,
    }
  );

  const latest: GameRequestProps[] | undefined = await getLatestGames(
    {
      period: 'week',
      _limit: 12,
      _gameRandom: true,
      _fieldsGame: 'image', // Include "image" property for each game.
    },
    {
      ssr: true,
      locale,
    }
  );

  if (!game?.id) {
    return {
      notFound: true,
      revalidate: parseInt(process.env.PROJECT_SSR_REVALIDATE_SECONDS || '') || 60,
    };
  }

  const page = {
    title: game?.name || null,
    body: game?.description || null,
    locale: context.locale,
  };
  const { title, body } = page;
  const pageContext = {
    locale: page.locale,
    locales: context.locales,
    defaultLocale: context.defaultLocale,
    // Todo: HUZ2ERA-977 - have the backend return translated slugs as part of the game object response.
    slugs:
      context?.locales?.map((locale) => ({
        locale,
        slug,
      })) || [],
    slug,
    breadcrumbs: mappingGameCrumbs(game, slug, locale),
  };
  const global = {
    popular: popular && Array.isArray(popular?.games) ? [...mappingGamesMinimal(popular.games)] : [],
    latest: Array.isArray(latest) ? [...mappingGamesMinimal(latest)] : [],
    category: category && Array.isArray(category.games) ? [...mappingGamesMinimal(category.games)] : [],
  };

  const seo = await getSEO();

  return {
    props: {
      title,
      body,
      seo,
      game: {
        ...mappingGame(game),
      },
      collections: [...(collections?.map((collection) => mappingCollection(collection)) || [])],
      global,
      pageContext,
      // Get our translations by namespace and locale.
      ...(await serverSideTranslations(locale as string, ['common', 'game', ...getNonPageNamespaces(locale)])),
    },
    // Incremental Static Regeneration (ISR).
    // https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration
    revalidate: parseInt(process.env.PROJECT_SSR_REVALIDATE_SECONDS || '') || 60,
  };
};
